import { BordOneToneIcon, NudosButton } from 'components/NudosComponents';
import { useEffect, useRef, useState } from 'react';
import { getStockOrderDetail } from 'services/stockOrders';
import { ICommentsAndObservations } from 'types/global';
import { IStockOrderDetail } from 'types/stockOrders';
import { submitComment } from 'views/LogisticServices/utils/general.utils';
import errorMessage from 'utils/errorMessage';
import CommentsContent from 'components/BordComentsAndObservations/components/CommentsContent/CommentsContent';
import { nudosGray500 } from 'styles/styles.constants';

import './CommentsInboxTrigger.scss';

const ArrowIcon = ({ isOpen }: { isOpen: boolean }) => {
  const variant = isOpen ? 'arrowHeadDown' : 'arrowHeadUp';
  return (
    <div className="arrowIcon">
      <BordOneToneIcon variant={variant} standardSize={10} stroke="white" viewBox="0 0 10 10" />
    </div>
  );
};

const CloseButton = ({ handleClick }: { handleClick: () => void }) => {
  return (
    <button onClick={handleClick}>
      <BordOneToneIcon variant="x" standardSize={10} stroke={nudosGray500} viewBox="0 0 10 10" />
    </button>
  );
};

interface ICommentsInboxTriggerProps {
  stockOrderData: IStockOrderDetail;
  uuid: string;
}

const CommentsInboxTrigger = ({
  stockOrderData: { orderId, stockOrderComments = [] },
  uuid
}: ICommentsInboxTriggerProps) => {
  const [showComments, setShowComments] = useState<boolean>(true);
  const [loadingComments, setLoadingComments] = useState<boolean>(false);
  const [dataComments, setDataComments] = useState<ICommentsAndObservations[]>([]);
  const commentsInboxRef = useRef<HTMLDivElement>(null);
  const commentsBoxRef = useRef<HTMLDivElement>(null);

  const handleShowComments = () => setShowComments(!showComments);

  const handleCommentsSubmission = async (comment: ICommentsAndObservations) => {
    if (!orderId) return;

    setLoadingComments(true);
    try {
      await submitComment({
        stockOrderId: orderId,
        allowNewCommentsSubmission: !loadingComments,
        handleLoading: setLoadingComments,
        comments: comment.comments,
        mentions: comment.mentions || [],
        commentImage: comment?.commentImage
      });
      const response = await getStockOrderDetail(uuid);
      setDataComments(response?.stockOrderComments || []);
    } catch (error) {
      errorMessage();
    } finally {
      setLoadingComments(false);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const isClickInCommentsBox = commentsBoxRef.current?.contains(event.target as Node);
    const isClickInCommentsInbox = commentsInboxRef.current?.contains(event.target as Node);
    if (showComments && !loadingComments && !isClickInCommentsBox && !isClickInCommentsInbox) {
      setShowComments(false);
    }
  };

  useEffect(() => {
    setDataComments(stockOrderComments);
  }, [stockOrderComments]);

  useEffect(() => {
    if (showComments) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showComments, loadingComments]);

  return (
    <div className="commentsInboxTrigger" ref={commentsInboxRef}>
      <NudosButton
        customClassName="buttonComments"
        buttonText="Comentarios"
        handleClick={handleShowComments}
        buttonIcon={<ArrowIcon isOpen={showComments} />}
      />
      {showComments && (
        <div className="commentsBox" ref={commentsBoxRef}>
          <div className="headerBox">
            <p className="titleLabel">Comentarios y Observaciones</p>
            <CloseButton handleClick={handleShowComments} />
          </div>
          <CommentsContent
            comments={dataComments}
            handleSubmitComments={handleCommentsSubmission}
            loading={loadingComments}
          />
        </div>
      )}
    </div>
  );
};

export default CommentsInboxTrigger;
